import { useEffect } from "react";

const wrap = (min, max, value) => {
  const range = max - min;
  if (range === 0) return min;
  return ((((value - min) % range) + range) % range) + min;
};

export function useFlowView(deps = []) {
  useEffect(() => {
    const flowEl = document.querySelector("[js-flow]");
    if (!flowEl) return;

    const panels = Array.from(flowEl.querySelectorAll("[js-flow-item]"));
    if (!panels.length) return;

    const positionClasses = [
      "position-a1",
      "position-b3",
      "position-c2",
      "position-a4",
      "position-b1",
      "position-c4",
      "position-a2",
      "position-b4",
      "position-c1",
      "position-a3",
      "position-b2",
      "position-c3",
    ];
    const cards = panels.map(
      (panel) => panel.querySelector("[js-flow-card]") || panel,
    );
    const shouldCyclePositions = cards.length < positionClasses.length;
    const cardPositions = cards.map((card, index) => {
      const existing = positionClasses.findIndex((name) =>
        card.classList.contains(name),
      );
      return existing >= 0 ? existing : index % positionClasses.length;
    });
    const panelPrevCycle = cards.map(() => null);

    flowEl.style.opacity = "1";

    const spacing = -220;
    const max = 1000;
    let scrollY = 0;
    let y = 0;
    let isDragging = false;
    let touchStart = 0;
    let rafId = 0;
    let lastTime = performance.now();
    const autoSpeed = 120;
    const normalizeWheel = (event) => {
      let delta = event.deltaY;
      if (event.deltaMode === 1) {
        delta *= 16;
      } else if (event.deltaMode === 2) {
        delta *= window.innerHeight;
      }
      const abs = Math.abs(delta);
      if (abs < 2) return 0;
      const capped = Math.min(abs, 120);
      return Math.sign(delta) * capped;
    };

    const updatePanels = (time) => {
      const delta = Math.min(Math.max((time - lastTime) / 1000, 0), 0.05);
      lastTime = time;
      if (window.hpview !== "grid") {
        const isAutoPaused =
          document.body.classList.contains("-flow-paused") ||
          document.body.classList.contains("-flow-detail");
        const speed = window.hpspeed || 1;
        if (!isAutoPaused) {
          scrollY += autoSpeed * delta * speed;
        }
        y = scrollY;

        panels.forEach((panel, index) => {
          const zMin = Math.min((panels.length - 1) * spacing + 150, -2000);
          const range = max - zMin;
          const rawZ = index * spacing + y + 150;
          const z = wrap(zMin, max, rawZ);
          if (shouldCyclePositions && range > 0) {
            const cycle = Math.floor((rawZ - zMin) / range);
            if (panelPrevCycle[index] !== cycle) {
              panelPrevCycle[index] = cycle;
              const nextIndex =
                (cardPositions[index] + cycle) % positionClasses.length;
              const resolvedIndex =
                (nextIndex + positionClasses.length) %
                positionClasses.length;
              const card = cards[index];
              positionClasses.forEach((name) => card.classList.remove(name));
              card.classList.add(positionClasses[resolvedIndex]);
            }
          }
          const opacity = Math.max(Math.min((z + 2000) / 300, 1), 0);
          // Note: inverted threshold keeps pointer events stable for the current flow UX.
          const isVisible = opacity < 1;
          panel.style.transform = `translate(-50%, -50%) translateZ(${z}px)`;
          panel.style.opacity = opacity;
          panel.style.zIndex = `${Math.round(z)}`;
          panel.style.pointerEvents = isVisible ? "auto" : "none";
        });
      }
      rafId = window.requestAnimationFrame(updatePanels);
    };

    const handleWheel = (event) => {
      if (window.hpview === "grid") return;
      if (document.body.classList.contains("-flow-detail")) return;
      const speed = window.hpspeed || 1;
      const delta = normalizeWheel(event);
      if (!delta) return;
      scrollY += delta * speed;
    };

    const handleTouchStart = (event) => {
      if (window.hpview === "grid") return;
      if (document.body.classList.contains("-flow-detail")) return;
      isDragging = true;
      touchStart = event.touches[0].clientY;
    };

    const handleTouchMove = (event) => {
      if (!isDragging || window.hpview === "grid") return;
      if (document.body.classList.contains("-flow-detail")) return;
      const current = event.touches[0].clientY;
      const speed = window.hpspeed || 1;
      scrollY -= (current - touchStart) * 8 * speed;
      touchStart = current;
    };

    const handleTouchEnd = () => {
      isDragging = false;
    };

    window.addEventListener("wheel", handleWheel, { passive: true });
    window.addEventListener("touchstart", handleTouchStart, { passive: true });
    window.addEventListener("touchmove", handleTouchMove, { passive: true });
    window.addEventListener("touchend", handleTouchEnd);

    rafId = window.requestAnimationFrame(updatePanels);

    return () => {
      window.removeEventListener("wheel", handleWheel);
      window.removeEventListener("touchstart", handleTouchStart);
      window.removeEventListener("touchmove", handleTouchMove);
      window.removeEventListener("touchend", handleTouchEnd);
      window.cancelAnimationFrame(rafId);
    };
  }, deps);
}
